import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  type ComponentProps,
  type ElementRef,
} from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { DismissableLayer } from '@radix-ui/react-dismissable-layer';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { Slot } from '@radix-ui/react-slot';
import { hideOthers } from 'aria-hidden';
import { RemoveScroll } from 'react-remove-scroll';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogPortal,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

type DialogProps = ComponentProps<typeof Dialog>;
type DialogContentProps = ComponentProps<typeof DialogContent>;

const CallCooperativeDialogContext = createContext({
  callActive: false,
  cooperative: false,
  open: false,
  close: () => {},
});

interface CallCooperativeDialogProps extends Omit<DialogProps, 'modal'> {
  callActive: boolean;
  /** Keep non-pulse users of a shared detail component on standard Radix. */
  cooperative?: boolean;
}

/**
 * A pulse dialog whose content tree stays mounted across call-state changes.
 *
 * Radix selects different ContentModal/ContentNonModal component types from
 * its `modal` prop, so changing that prop remounts rich editors. Cooperative
 * pulses keep the Root non-modal and pair it with the stable content below,
 * which uses Radix's own FocusScope/DismissableLayer primitives to provide
 * real modal behavior while idle and relax only the call boundary in-call.
 */
export function CallCooperativeDialog({
  callActive,
  cooperative = true,
  ...props
}: CallCooperativeDialogProps) {
  if (!cooperative) return <Dialog {...props} />;

  return (
    <CallCooperativeDialogContext.Provider
      value={{
        callActive,
        cooperative,
        open: !!props.open,
        close: () => props.onOpenChange?.(false),
      }}
    >
      <Dialog modal={false} {...props} />
    </CallCooperativeDialogContext.Provider>
  );
}

function focusableElements(container: ParentNode) {
  return Array.from(container.querySelectorAll<HTMLElement>(
    'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [href], [contenteditable="true"], [tabindex]:not([tabindex="-1"])',
  )).filter(element => element.getAttribute('aria-hidden') !== 'true');
}

export const CallCooperativeDialogContent = forwardRef<
  ElementRef<typeof DialogContent>,
  DialogContentProps
>(({
  className,
  children,
  onFocusOutside,
  onPointerDownOutside,
  onInteractOutside,
  onEscapeKeyDown,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onKeyDown,
  forceMount: _forceMount,
  ...props
}, forwardedRef) => {
  const { callActive, cooperative, open, close } = useContext(CallCooperativeDialogContext);
  const localRef = useRef<HTMLDivElement | null>(null);
  const lastPulseFocusRef = useRef<HTMLElement | null>(null);
  const previousCallActiveRef = useRef(callActive);

  useEffect(() => {
    if (!cooperative || !open) return;
    const content = localRef.current;
    if (!content) return;
    const callSurface = content.ownerDocument.querySelector<HTMLElement>(
      '[data-call-focus-surface="true"]',
    );
    return hideOthers(callActive && callSurface ? [content, callSurface] : content);
  }, [callActive, cooperative, open]);

  // Re-establish idle containment after a call ends only when focus was on
  // the call. A nested Select/Popover or priority dialog keeps its own focus.
  useEffect(() => {
    const wasActive = previousCallActiveRef.current;
    previousCallActiveRef.current = callActive;
    if (!cooperative || !wasActive || callActive) return;
    const active = localRef.current?.ownerDocument.activeElement;
    if (active instanceof HTMLElement && active.closest('[data-call-focus-surface="true"]')) {
      const target = lastPulseFocusRef.current;
      if (target?.isConnected) target.focus();
      else localRef.current?.focus();
    }
  }, [callActive, cooperative]);

  if (!cooperative) {
    return (
      <DialogContent
        {...props}
        className={className}
        ref={forwardedRef}
        onFocusOutside={onFocusOutside}
        onPointerDownOutside={onPointerDownOutside}
        onInteractOutside={onInteractOutside}
        onEscapeKeyDown={onEscapeKeyDown}
        onOpenAutoFocus={onOpenAutoFocus}
        onCloseAutoFocus={onCloseAutoFocus}
        onKeyDown={onKeyDown}
      >
        {children}
      </DialogContent>
    );
  }

  return (
    <DialogPortal>
      <RemoveScroll as={Slot} enabled={!callActive} allowPinchZoom shards={[localRef]}>
        <div
          data-testid="pulse-dialog-overlay"
          data-state={open ? 'open' : 'closed'}
          className="fixed inset-0 z-50 bg-black/80 pointer-events-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
      </RemoveScroll>
      <FocusScope
        asChild
        loop={!callActive}
        trapped={!callActive}
        onMountAutoFocus={onOpenAutoFocus}
        onUnmountAutoFocus={onCloseAutoFocus}
      >
        <DismissableLayer
          {...props}
          ref={(node) => {
            localRef.current = node;
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
          }}
          role="dialog"
          aria-modal={!callActive}
          data-state={open ? 'open' : 'closed'}
          disableOutsidePointerEvents={!callActive}
          className={cn(
            'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg',
            className,
          )}
          onFocusCapture={(event) => {
            lastPulseFocusRef.current = event.target as HTMLElement;
            props.onFocusCapture?.(event);
          }}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (event.defaultPrevented || !callActive || event.key !== 'Tab') return;
            const content = localRef.current;
            const callSurface = content?.ownerDocument.querySelector<HTMLElement>(
              '[data-call-focus-surface="true"]',
            );
            if (!content || !callSurface) return;
            const pulseTargets = focusableElements(content);
            const callTargets = focusableElements(callSurface);
            const forward = !event.shiftKey &&
              document.activeElement === pulseTargets[pulseTargets.length - 1];
            const backward = event.shiftKey && document.activeElement === pulseTargets[0];
            if (callTargets.length && (forward || backward)) {
              event.preventDefault();
              (backward ? callTargets[callTargets.length - 1] : callTargets[0]).focus();
            }
          }}
          onEscapeKeyDown={onEscapeKeyDown}
          onFocusOutside={onFocusOutside}
          onPointerDownOutside={onPointerDownOutside}
          onInteractOutside={(event) => {
            onInteractOutside?.(event);
            // During coexistence, clicking/focusing the call must not discard
            // the pulse. Idle mode retains Radix's normal backdrop dismissal.
            if (callActive && !event.defaultPrevented) event.preventDefault();
          }}
          onDismiss={close}
        >
          {children}
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DismissableLayer>
      </FocusScope>
    </DialogPortal>
  );
});
CallCooperativeDialogContent.displayName = 'CallCooperativeDialogContent';